import {
  Breadcrumb,
  BreadcrumbLink,
  BreadcrumbPage,
  Button,
  Pagination,
  Switch,
  Tag,
  TagSelect,
} from '@ads/components-react';
import { CheckIcon, ClipboardDocumentListIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type {
  QuestionTypeFilter,
  RevisionExamDetail,
  RevisionQuestion,
} from '@/features/exam-revision/types/revision';
import {
  questionFilterI18nKey,
  questionTypeI18nKey,
} from '@/features/exam-revision/utils/questionType';
import { RichTextEditor, Skeleton } from '@/shared/ui/atoms';
import { PageHeader } from '@/shared/ui/organisms/PageHeader';

/**
 * Props do template de revisão de questões de uma prova gerada por IA.
 */
export interface RevisionReviewTemplateProps {
  /** Detalhes do exame em revisão (ID, título, disciplina, data) */
  readonly exam: RevisionExamDetail;
  /** Lista de questões filtradas disponíveis para navegação */
  readonly questions: RevisionQuestion[];
  /** Questão atualmente em foco para edição */
  readonly current: RevisionQuestion | null;
  /** Índice (0-based) da questão atual dentro de `questions` */
  readonly currentIndex: number;
  /** Total de questões no filtro ativo */
  readonly total: number;
  /** Filtro de tipo de questão ativo */
  readonly typeFilter: QuestionTypeFilter;
  /** Opções de filtro a exibir ("Todos" + tipos presentes na prova) */
  readonly availableFilters: readonly QuestionTypeFilter[];
  /** Callback ao selecionar um filtro de tipo */
  readonly onTypeFilter: (type: QuestionTypeFilter) => void;
  /** Callback ao navegar para um índice específico */
  readonly onNavigate: (index: number) => void;
  /** Callback ao atualizar campos de uma questão */
  readonly onUpdateQuestion: (id: number, patch: Partial<RevisionQuestion>) => void;
  /** Callback ao excluir uma questão pelo ID (abre o modal de confirmação) */
  readonly onDeleteQuestion: (id: number) => void;
  /** Callback ao acionar "Adicionar Questão" */
  readonly onAddQuestion: () => void;
  /** Callback ao salvar individualmente a questão em foco (`PUT /question`) */
  readonly onSave: () => void;
  /** Callback ao finalizar a revisão (`POST /review`) */
  readonly onFinalize: () => void;
  /** Sinaliza que a questão em foco está sendo salva */
  readonly isSaving?: boolean;
  /** Sinaliza que a revisão está sendo finalizada/submetida */
  readonly isFinalizing?: boolean;
  /** Indica se os dados ainda estão carregando */
  readonly isLoading: boolean;
}

/**
 * Template da tela de revisão de questões de uma prova gerada por IA.
 *
 * Exibe cabeçalho com breadcrumb e contagem de questões, uma barra de filtros
 * por tipo (pills), navegação entre questões, card do editor (enunciado em rich
 * text, trecho da aula e explicação), card de alternativas com indicador de
 * resposta correta, e rodapé com paginação e botão de finalizar.
 *
 * @param exam - Detalhes do exame em revisão
 * @param questions - Lista de questões filtradas
 * @param current - Questão em foco
 * @param currentIndex - Índice atual (0-based)
 * @param total - Total de questões no filtro ativo
 * @param typeFilter - Filtro de tipo selecionado
 * @param onTypeFilter - Altera o filtro de tipo
 * @param onNavigate - Navega para um índice específico
 * @param onUpdateQuestion - Atualiza campos da questão atual
 * @param onDeleteQuestion - Exclui a questão atual
 * @param onAddQuestion - Abre fluxo de adição de questão
 * @param onFinalize - Finaliza a revisão
 * @param isLoading - Estado de carregamento
 *
 * @example
 * ```tsx
 * <RevisionReviewTemplate
 *   exam={exam}
 *   questions={questions}
 *   current={current}
 *   currentIndex={0}
 *   total={36}
 *   typeFilter="Todos"
 *   onTypeFilter={setTypeFilter}
 *   onNavigate={goTo}
 *   onUpdateQuestion={updateQuestion}
 *   onDeleteQuestion={deleteQuestion}
 *   onAddQuestion={() => {}}
 *   onFinalize={handleFinalize}
 *   isLoading={false}
 * />
 * ```
 */
export function RevisionReviewTemplate({
  exam,
  current,
  currentIndex,
  total,
  typeFilter,
  availableFilters,
  onTypeFilter,
  onNavigate,
  onUpdateQuestion,
  onDeleteQuestion,
  onAddQuestion,
  onSave,
  onFinalize,
  isSaving = false,
  isFinalizing = false,
  isLoading,
}: RevisionReviewTemplateProps) {
  const { t } = useTranslation('examRevision');

  if (isLoading) {
    return <RevisionReviewSkeleton />;
  }

  if (!current) {
    return (
      <div className="flex flex-col items-center justify-center gap-nano rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface py-xl">
        <ClipboardDocumentListIcon className="h-[2.5rem] w-[2.5rem] text-ctx-content-placeholder" />
        <span className="ts-paragraph-xs text-ctx-content-placeholder">
          {t('review.noQuestion')}
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-mini">
      <PageHeader
        title={t('review.headerTitle', { title: exam.title })}
        subtitle={t('review.count', { count: total })}
        breadcrumb={
          <Breadcrumb>
            <BreadcrumbLink>
              <Link to="/">{t('breadcrumb.home')}</Link>
            </BreadcrumbLink>
            <BreadcrumbLink>
              <Link to={'/exams' as never}>{t('breadcrumb.exams')}</Link>
            </BreadcrumbLink>
            <BreadcrumbLink>
              <Link to={'/exam/revision' as never}>{t('breadcrumb.revision')}</Link>
            </BreadcrumbLink>
            <BreadcrumbPage>{exam.title}</BreadcrumbPage>
          </Breadcrumb>
        }
        actions={
          <Button hierarchy="primary" size="md" leadingIcon={PlusIcon} onClick={onAddQuestion}>
            {t('review.addQuestion')}
          </Button>
        }
      />

      {/* Type Filter Pills */}
      <div className="flex flex-wrap items-center gap-nano">
        <span className="text-xxs font-bold text-ctx-content-base">{t('review.filterLabel')}</span>
        {availableFilters.map(filter => (
          <TagSelect
            size="sm"
            custom={{ label: 'text-nowrap' }}
            active={typeFilter === filter}
            onClick={() => onTypeFilter(filter)}
            key={String(filter)}
            label={t(questionFilterI18nKey(filter))}
          />
        ))}
      </div>

      {/* Question Nav Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-nano">
          <ClipboardDocumentListIcon className="h-mini w-mini text-ctx-content-base" />
          <span className="ts-heading-xs text-ctx-content-title">
            {t('review.questionLabel', { number: current.index })}
          </span>
          <Tag
            size="sm"
            custom={{ text: 'text-nowrap' }}
            label={t(questionTypeI18nKey(current.questionType))}
          />
        </div>
        <div className="flex items-center gap-nano">
          <Button
            hierarchy="secondary"
            size="sm"
            disabled={currentIndex === 0}
            onClick={() => onNavigate(currentIndex - 1)}
          >
            {t('review.previous')}
          </Button>
          <Button
            hierarchy="secondary"
            size="sm"
            disabled={currentIndex >= total - 1}
            onClick={() => onNavigate(currentIndex + 1)}
          >
            {t('review.next')}
          </Button>
        </div>
      </div>

      {/* Editor Card */}
      <div className="overflow-hidden rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-mini border-b-hairline border-ctx-layout-border bg-ctx-layout-body px-xs py-micro">
          <div className="flex items-center gap-mini">
            <div className="flex items-center gap-nano">
              <span className="ts-subtitle-xxs text-ctx-content-base">
                {t('review.toolbar.reviewed')}
              </span>
              <Switch
                checked={current.isReviewed}
                onCheckedChange={checked =>
                  onUpdateQuestion(current.id, { isReviewed: Boolean(checked) })
                }
                size="sm"
              />
              <span className="ts-subtitle-xxs text-ctx-content-title">
                {current.isReviewed
                  ? t('review.toolbar.reviewedYes')
                  : t('review.toolbar.reviewedNo')}
              </span>
            </div>
            <Button
              hierarchy="secondary"
              size="sm"
              leadingIcon={CheckIcon}
              onClick={onSave}
              isLoading={isSaving}
            >
              {t('review.toolbar.save')}
            </Button>
          </div>
          <div className="flex items-center gap-mini">
            <div className="flex items-center gap-nano">
              <span className="ts-subtitle-xxs text-ctx-content-base">
                {t('review.toolbar.retranslateAI')}
              </span>
              <Switch
                checked={current.retranslateWithAI}
                onCheckedChange={checked =>
                  onUpdateQuestion(current.id, {
                    retranslateWithAI: Boolean(checked),
                  })
                }
                size="sm"
              />
              <span
                className={
                  current.retranslateWithAI
                    ? 'ts-subtitle-xxs text-ctx-content-title'
                    : 'ts-paragraph-xs text-ctx-content-placeholder'
                }
              >
                {current.retranslateWithAI
                  ? t('review.toolbar.retranslateYes')
                  : t('review.toolbar.retranslateNo')}
              </span>
            </div>
            <Button hierarchy="danger" size="sm" onClick={() => onDeleteQuestion(current.id)}>
              {t('review.toolbar.delete')}
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="flex flex-col gap-mini p-xs">
          <div className="flex flex-col gap-nano">
            <span className="text-xxs font-bold text-ctx-content-title">
              {t('review.sections.label')}
            </span>
            <RichTextEditor
              key={`${current.id}-label`}
              value={current.label}
              onChange={v => onUpdateQuestion(current.id, { label: v })}
            />
          </div>

          <div className="flex flex-col gap-nano">
            <span className="text-xxs font-bold text-ctx-content-title">
              {t('review.sections.lessonExcerpt')}
            </span>
            <RichTextEditor
              key={`${current.id}-excerpt`}
              value={current.lessonExcerpt}
              onChange={v => onUpdateQuestion(current.id, { lessonExcerpt: v })}
            />
          </div>

          <div className="flex flex-col gap-nano">
            <span className="text-xxs font-bold text-ctx-content-title">
              {t('review.sections.explanation')}
            </span>
            <RichTextEditor
              key={`${current.id}-explanation`}
              value={current.explanation}
              onChange={v => onUpdateQuestion(current.id, { explanation: v })}
            />
          </div>
        </div>
      </div>

      {/* Alternatives Card */}
      <div className="flex flex-col gap-mini rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xs">
        <span className="ts-heading-sm text-ctx-content-title">{t('review.alternatives')}</span>
        {current.alternatives.map(alt => (
          <div key={`${current.id}-${alt.id}`} className="flex items-start gap-mini">
            <div className="py-nano">
              <button
                type="button"
                aria-label={t('review.markCorrect')}
                aria-pressed={alt.isCorrect}
                onClick={() =>
                  onUpdateQuestion(current.id, {
                    alternatives: current.alternatives.map(a => ({
                      ...a,
                      isCorrect: a.id === alt.id,
                    })),
                  })
                }
                className={[
                  'flex h-xxxs w-xxxs items-center justify-center rounded-pill border-thin transition-colors',
                  alt.isCorrect
                    ? 'border-ctx-interactive-primary'
                    : 'border-base-gray-400 hover:border-ctx-interactive-primary',
                ].join(' ')}
              >
                {alt.isCorrect && (
                  <span className="h-[0.625rem] w-[0.625rem] rounded-pill bg-ctx-interactive-primary" />
                )}
              </button>
            </div>
            <div className="flex-1">
              <RichTextEditor
                value={alt.text}
                onChange={v => {
                  onUpdateQuestion(current.id, {
                    alternatives: current.alternatives.map(a =>
                      a.id === alt.id ? { ...a, text: v } : a,
                    ),
                  });
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Footer with Pagination */}
      <div className="flex items-center gap-mini">
        <div className="flex-1" />
        <Pagination
          pageAmount={total}
          activeIndex={currentIndex}
          setActiveIndex={v => onNavigate(typeof v === 'function' ? v(currentIndex) : v)}
          ellipsisLabel="…"
        />
        <div className="flex flex-1 justify-end">
          <Button
            hierarchy="primary"
            size="md"
            leadingIcon={CheckIcon}
            onClick={onFinalize}
            isLoading={isFinalizing}
          >
            {t('review.footer.finalize')}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Esqueleto de carregamento da tela de revisão de questões, espelhando a estrutura
 * real (cabeçalho, pills de filtro, barra de navegação, card do editor e card de
 * alternativas) para uma transição suave enquanto as questões são carregadas.
 */
function RevisionReviewSkeleton() {
  return (
    <div className="flex flex-col gap-mini" aria-hidden="true">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-nano">
          <Skeleton className="h-xs w-[18rem] rounded-sm" />
          <Skeleton className="h-xxs w-[8rem] rounded-sm" />
        </div>
        <Skeleton className="h-[2.5rem] w-[11rem] rounded-sm" />
      </div>

      {/* Filter pills */}
      <div className="flex flex-wrap items-center gap-nano">
        <Skeleton className="h-xxs w-[6rem] rounded-sm" />
        {[0, 1, 2, 3].map(i => (
          <Skeleton key={i} className="h-[1.75rem] w-[5.5rem] rounded-pill" />
        ))}
      </div>

      {/* Question nav bar */}
      <div className="flex items-center justify-between">
        <Skeleton className="h-xs w-[12rem] rounded-sm" />
        <div className="flex items-center gap-nano">
          <Skeleton className="h-[2rem] w-[6rem] rounded-sm" />
          <Skeleton className="h-[2rem] w-[6rem] rounded-sm" />
        </div>
      </div>

      {/* Editor card */}
      <div className="flex flex-col gap-mini rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xs">
        <Skeleton className="h-[2.5rem] w-full rounded-sm" />
        <Skeleton className="h-[8rem] w-full rounded-sm" />
        <Skeleton className="h-[4rem] w-full rounded-sm" />
        <Skeleton className="h-[4rem] w-full rounded-sm" />
      </div>

      {/* Alternatives card */}
      <div className="flex flex-col gap-mini rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xs">
        <Skeleton className="h-xs w-[8rem] rounded-sm" />
        {[0, 1, 2, 3].map(i => (
          <Skeleton key={i} className="h-[3rem] w-full rounded-sm" />
        ))}
      </div>
    </div>
  );
}
